<template>
  <div
    ref="rootEl"
    class="trustpilot-widget"
    :data-locale="resolvedLocale"
    :data-template-id="templateId"
    :data-businessunit-id="resolvedBusinessUnitId"
    :data-style-height="styleHeight"
    :data-style-width="styleWidth"
    :data-theme="theme"
    :data-tags="tags"
    :data-schema-type="schemaType"
    :data-stars="stars"
    :data-review-languages="reviewLanguages"
  >
    <a :href="reviewUrl">Trustpilot</a>
  </div>
</template>

<script setup lang="ts">
import { TRUSTPILOT_BUSINESS_UNIT_ID, TRUSTPILOT_BUSINESS_UNIT_NAME } from '~~/constants/trustPilot';

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: Element) => void;
    };
  }
}

type Theme = 'light' | 'dark';

const props = withDefaults(
  defineProps<{
    templateId: string;
    businessunitId?: string;
    businessunitName?: string;
    locale?: string;
    styleHeight?: string;
    styleWidth?: string;
    theme?: Theme;
    tags?: string;
    schemaType?: string;
    stars?: string;
    reviewLanguages?: string;
  }>(),
  {
    locale: 'en-US',
    styleHeight: 'auto',
    styleWidth: '100%',
    theme: 'light',
  },
);

const rootEl = ref<HTMLElement | null>(null);
const nuxtApp = useNuxtApp();

const resolvedBusinessUnitId = computed(() => {

  return props.businessunitId || TRUSTPILOT_BUSINESS_UNIT_ID || '';

});

const resolvedBusinessUnitName = computed(() => {

  return props.businessunitName || TRUSTPILOT_BUSINESS_UNIT_NAME || '';

});

const resolvedLocale = computed(() => props.locale || 'en-US');

const reviewUrl = computed(() => {

  const localeLanguage = (resolvedLocale.value.split('-')[0] || 'en').toLowerCase();
  const prefix = localeLanguage === 'en' ? '' : `${localeLanguage}.`;
  const business = resolvedBusinessUnitName.value;

  return `https://${prefix}trustpilot.com/review/${business}`;

});

async function loadWidget() {

  if (import.meta.server) {

    return;
  
  }

  await (nuxtApp.$trustpilotReady as Promise<void> | undefined);
  await nextTick();

  const el = rootEl.value;

  if (!el) {

    return;
  
  }

  window.Trustpilot?.loadFromElement(el);

}

onMounted(() => {

  void loadWidget();

});

watch(
  () => [
    props.templateId,
    resolvedBusinessUnitId.value,
    resolvedBusinessUnitName.value,
    resolvedLocale.value,
    props.styleHeight,
    props.styleWidth,
    props.theme,
    props.tags,
    props.schemaType,
    props.stars,
    props.reviewLanguages,
  ],
  async () => {

    await loadWidget();
  
  },
);
</script>
